import { IonButton, IonSkeletonText } from '@ionic/react';
import { format, subYears } from 'date-fns';
import React, { lazy, useCallback, useMemo } from 'react';
import OnboardingPageWrapper from "../OnboardingPageWrapper/OnboardingPageWrapper";
import { useSignUp } from "../SignUpProvider/SignUpProvider";
import './BirthdayStep.css';

// Lazy load the IonDatetime component
const IonDatetime = lazy(() => import('@ionic/react').then(module => ({
    default: module.IonDatetime
})));

interface BirthdayStepProps {
    onNext: () => void;
    title: string;
    showBackButton: boolean;
    showSkipButton: boolean;
    onBack?: () => void;
}

const BirthdayStep: React.FC<BirthdayStepProps> = ({
    onNext,
    showSkipButton,
    showBackButton,
    title,
    onBack
}) => {
    const { signUpData, updateSignUpData } = useSignUp();

    const dates = useMemo(() => {
        return {
            startDate: format(subYears(new Date(), 18), 'yyyy-MM-dd'),
        };
    }, []);

    const handleDateChange = useCallback(async (event: CustomEvent) => {
        const value = event.detail.value;
        if (typeof value === 'string') {
            await updateSignUpData({ birthday: value });
        } else if (Array.isArray(value) && value.length > 0) {
            await updateSignUpData({ birthday: value[0] });
        }
    }, [updateSignUpData]);

    // Simple loading state component
    const LoadingState = () => (
        <div className="w-full h-48 bg-primary border border-electric-blue rounded-lg p-4">
            {/* Wheels */}
            <div className="flex justify-center items-center gap-4 h-32">
                {/* Month wheel */}
                <div className="flex flex-col space-y-2 w-20">
                    <IonSkeletonText
                        animated={true}
                        className="h-8 w-full rounded-md"
                    />
                    <IonSkeletonText
                        animated={true}
                        className="h-8 w-full rounded-md"
                    />
                    <IonSkeletonText
                        animated={true}
                        className="h-8 w-full rounded-md"
                    />
                    <IonSkeletonText
                        animated={true}
                        className="h-8 w-full rounded-md"
                    />
                </div>

                {/* Day wheel */}
                <div className="flex flex-col space-y-2 w-16">
                    <IonSkeletonText
                        animated={true}
                        className="h-8 w-full rounded-md"
                    />
                    <IonSkeletonText
                        animated={true}
                        className="h-8 w-full rounded-md"
                    />
                    <IonSkeletonText
                        animated={true}
                        className="h-8 w-full rounded-md"
                    />
                    <IonSkeletonText
                        animated={true}
                        className="h-8 w-full rounded-md"
                    />
                </div>

                {/* Year wheel */}
                <div className="flex flex-col space-y-2 w-20">
                    <IonSkeletonText
                        animated={true}
                        className="h-8 w-full rounded-md"
                    />
                    <IonSkeletonText
                        animated={true}
                        className="h-8 w-full rounded-md"
                    />
                    <IonSkeletonText
                        animated={true}
                        className="h-8 w-full rounded-md"
                    />
                    <IonSkeletonText
                        animated={true}
                        className="h-8 w-full rounded-md"
                    />
                </div>
            </div>
        </div>
    );

    return (
        <OnboardingPageWrapper
            title={title}
            showBackButton={showBackButton}
            showSkipButton={showSkipButton}
            onBack={onBack}
        >
            <div className="w-full h-full max-w-md space-y-6 flex flex-col items-center">
                <IonDatetime
                    presentation="date"
                    value={signUpData.birthday || dates.startDate}
                    preferWheel={true}
                    className="ion-datetime-custom bg-primary border border-electric-blue b-2 rounded-lg w-full"
                    color="dark"
                    onIonChange={handleDateChange}
                />
                <IonButton
                    expand="block"
                    className="neon-button-alt w-full normal-case font-bold"
                    onClick={onNext}
                    disabled={!signUpData.birthday}
                >
                    Continue
                </IonButton>
            </div>
        </OnboardingPageWrapper>
    );
};

export default BirthdayStep;